import { getSubtleSurfaceBackground } from '@app/core/ui/subtle-surface';
import { Box, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Group, Panel, PanelProps, Separator, useDefaultLayout } from 'react-resizable-panels';
import { DRAWER_PANEL_ID, MAIN_PANEL_ID } from '../page-layout-constants';
import { PageDrawerContent } from './PageDrawerContent';
import { PageDrawerContextProvider } from './PageDrawerContext';

const MAIN_PANEL_STYLE: React.CSSProperties = {
  minHeight: 0,
  minWidth: 0,
  overflowX: 'hidden', // Clip horizontal overflow (waveform/timeline can render > container width)
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const DRAWER_PANEL_STYLE: React.CSSProperties = {
  minHeight: 0,
  overflow: 'auto',
};

export interface PageDrawerProps {
  readonly children: React.ReactNode;
  /** Optional group id for layout persistence. When set, this page's drawer size is stored separately (e.g. per route). */
  readonly drawerId?: string;
  /** Content to render in the drawer when open (e.g. StreamDrawerContent for the stream page). */
  readonly drawerContent?: React.ReactNode;
  /** Initial open state (e.g. from persisted store). When provided, drawer starts open/closed to match store after hydration. */
  readonly initialOpen?: boolean;
  /** When provided (e.g. by stream page), called when openDrawer is invoked so the page can persist content type. */
  readonly onOpen?: (contentType?: string) => void;
  /** When provided (e.g. by stream page), called when closeDrawer is invoked so the page can run teardown (e.g. run stored onClose callback). */
  readonly onClose?: () => void;
  /** Panel configuration for the drawer. */
  readonly panelConfig?: PanelProps;
}

/**
 * Layout that provides drawer context and renders resizable main + drawer panels.
 * When drawerId is set, renders main panel, separator, and collapsible drawer panel.
 * When drawerId is undefined, renders only full-width content (no drawer, no resize handle).
 */
export function PageDrawer({
  children,
  drawerId,
  drawerContent,
  initialOpen,
  onOpen,
  onClose,
  panelConfig,
}: PageDrawerProps) {
  const [open, setOpen] = useState(initialOpen ?? false);
  const theme = useTheme();
  const separatorStyle: React.CSSProperties = {
    width: 6,
    minWidth: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getSubtleSurfaceBackground(theme),
    boxShadow: 'inset 1px 0 2px rgba(0,0,0,0.1), inset -1px 0 2px rgba(0,0,0,0.1)',
  };

  const openDrawer = (contentType?: string) => {
    setOpen(true);
    onOpen?.(contentType);
  };

  const closeDrawer = () => {
    setOpen(false);
    onClose?.();
  };

  const contextValue = useMemo(
    () => ({
      open,
      openDrawer,
      closeDrawer,
    }),
    [open, openDrawer, closeDrawer],
  );

  const groupId = drawerId ?? 'default';
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: groupId,
    panelIds: drawerId && open ? [MAIN_PANEL_ID, DRAWER_PANEL_ID] : [MAIN_PANEL_ID],
  });

  return (
    <PageDrawerContextProvider value={contextValue}>
      <Group
        id={groupId}
        orientation="horizontal"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        <Panel id={MAIN_PANEL_ID} minSize={'40%'} style={MAIN_PANEL_STYLE} tabIndex={0}>
          {children}
        </Panel>
        {open && (
          <>
            <Separator style={separatorStyle}>
              <Box
                sx={{
                  width: 3,
                  height: 48,
                  borderRadius: 3,
                  backgroundColor: 'divider',
                }}
              />
            </Separator>
            <Panel
              collapsible
              id={DRAWER_PANEL_ID}
              defaultSize={480}
              minSize={320}
              maxSize={600}
              style={DRAWER_PANEL_STYLE}
              {...panelConfig}
            >
              <PageDrawerContent content={drawerContent} />
            </Panel>
          </>
        )}
      </Group>
    </PageDrawerContextProvider>
  );
}
